import { zodResolver } from '@hookform/resolvers/zod';
import Button from '@rio-cloud/rio-uikit/Button';
import ButtonToolbar from '@rio-cloud/rio-uikit/ButtonToolbar';
import Dialog from '@rio-cloud/rio-uikit/Dialog';
import { useEffect, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useIntl } from 'react-intl';

import { mapStopToUpdateStop } from '#api/adapters/stopAdapter.ts';
import type { ApiUpdateStop } from '#api/model/types.ts';
import { ChargingConfigurationFields } from '#components/tourStops/ChargingConfigurationFields.tsx';
import { useFeatureToggle } from '#configuration/featureFlags/useFeatureToggle.ts';
import { chargingConfigurationSchema } from '#domain/ChargingDetails.ts';
import { isCoordinateOnlyServiceStop, type Stop } from '#domain/Stop.ts';
import { LocationFormView } from '#features/orderForm/ui/LocationForm.tsx';

import {
    type EditStopFormData,
    getEditStopFormValidation,
    mapEditStopFormDataToStop,
    mapStopToEditStopFormData,
} from '../model/EditStopFormState.ts';
import { mapStopTypeToAddressType } from '../model/mapStopTypeToAddressType.ts';

type EditStopDialogProps = {
    allowCharging?: boolean;
    stop?: Stop;
    isOpen: boolean;
    isLoading?: boolean;
    onClose: () => void;
    onSubmit: (stopUpdate: ApiUpdateStop, updatedStop: Stop) => Promise<void> | void;
    validateStop?: (updatedStop: Stop) => { field: 'startDate' | 'endDate'; message: string } | undefined;
};

const validCoordinates = (latitude: number | undefined, longitude: number | undefined) =>
    latitude !== undefined &&
    longitude !== undefined &&
    Number.isFinite(latitude) &&
    Number.isFinite(longitude) &&
    Math.abs(latitude) <= 90 &&
    Math.abs(longitude) <= 180;

export const EditStopDialog = ({
    stop,
    isOpen,
    isLoading = false,
    onClose,
    onSubmit,
    validateStop,
    allowCharging = false,
}: EditStopDialogProps) => {
    const intl = useIntl();
    const showCharging = useFeatureToggle('showSmartRoute', false);
    const [charging, setCharging] = useState(stop?.charging?.configuration);
    const [chargingError, setChargingError] = useState(false);
    useEffect(() => {
        setCharging(stop?.charging?.configuration);
        setChargingError(false);
    }, [stop?.id, isOpen]);
    const formContext = useForm<EditStopFormData>({
        resolver: zodResolver(
            getEditStopFormValidation(stop?.charging?.source === 'here' || isCoordinateOnlyServiceStop(stop))
        ),
        mode: 'onTouched',
        values: stop ? mapStopToEditStopFormData(stop) : undefined,
    });
    const {
        handleSubmit,
        setError,
        clearErrors,
        setValue,
        formState: { errors },
    } = formContext;
    const [latitude, longitude] = useWatch({ control: formContext.control, name: ['latitude', 'longitude'] });

    useEffect(() => {
        if (validCoordinates(latitude, longitude)) {
            clearErrors('latitude');
        }
    }, [clearErrors, latitude, longitude]);

    const addressType = mapStopTypeToAddressType(stop?.type);
    const onAddressSuggestionSelected = (selectedLocation: EditStopFormData['location']) => {
        if (validCoordinates(selectedLocation.latitude, selectedLocation.longitude)) {
            setValue('latitude', selectedLocation.latitude);
            setValue('longitude', selectedLocation.longitude);
        } else {
            setValue('latitude', undefined);
            setValue('longitude', undefined);
            setValue('location.latitude', undefined);
            setValue('location.longitude', undefined);
        }
    };

    const submit = handleSubmit(async formValues => {
        if (!stop) {
            return;
        }
        const updatedStop = mapEditStopFormDataToStop(formValues, stop);
        if (charging && !chargingConfigurationSchema.safeParse(charging).success) {
            setChargingError(true);
            return;
        }
        updatedStop.charging = charging
            ? { configuration: charging, source: stop.charging?.source ?? 'manual' }
            : undefined;
        updatedStop.clearCharging = !charging && Boolean(stop.charging || stop.clearCharging);
        const addressChanged = (['street', 'city', 'zipCode', 'country'] as const).some(
            field => stop.location[field] !== updatedStop.location[field]
        );
        if (addressChanged && !validCoordinates(formValues.latitude, formValues.longitude)) {
            setError('latitude', {
                type: 'manual',
                message: intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.tours.create-dialog.route-preview.no-coordinates',
                }),
            });
            return;
        }
        const validationError = validateStop?.(updatedStop);

        if (validationError) {
            setError(validationError.field, { type: 'manual', message: validationError.message });
            return;
        }

        await onSubmit(mapStopToUpdateStop(updatedStop), updatedStop);
        onClose();
    });

    return (
        <FormProvider {...formContext}>
            <Dialog
                show={isOpen}
                title={intl.formatMessage({
                    id: 'intl-msg:ordercom-cloud-frontend.tours.stop.edit.title',
                })}
                body={
                    <>
                        <LocationFormView
                            addressDisabled
                            addressType={addressType}
                            allowPastDates
                            commentName='comment'
                            onAddressSuggestionSelected={onAddressSuggestionSelected}
                            showCommentInitially={Boolean(stop?.comment)}
                        />
                        {errors.latitude?.message && <p role='alert'>{errors.latitude.message}</p>}
                        {showCharging && (allowCharging || stop?.charging) && (
                            <ChargingConfigurationFields value={charging} onChange={setCharging} />
                        )}
                        {chargingError && (
                            <p role='alert'>
                                {intl.formatMessage({ id: 'intl-msg:ordercom-cloud-frontend.charging.invalid' })}
                            </p>
                        )}
                    </>
                }
                footer={
                    <ButtonToolbar alignment='right'>
                        <Button bsStyle='default' onClick={onClose}>
                            {intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.common.cancel',
                            })}
                        </Button>
                        <Button bsStyle='primary' onClick={() => submit()} disabled={isLoading}>
                            {intl.formatMessage({
                                id: 'intl-msg:ordercom-cloud-frontend.orders.edit-dialog.save',
                            })}
                        </Button>
                    </ButtonToolbar>
                }
                onClose={onClose}
                bsSize='md'
                showCloseButton
            />
        </FormProvider>
    );
};
